import React from "react";
import { Field, Form, Formik } from "formik";

import * as config from "util/config";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import { bool, string } from "prop-types";

import { actions as recoveryPasswordActions } from "reducers/recoveryPassword";

import Button from "pages/_components/Button";
import I18n from "pages/_components/I18n";
import Credential from "pages/_components/fields/credentials/Credential";
import Logo from "pages/login/_components/Logo";

import ToDoList from "pages/onboarding/_components/ToDoList";
import { useDispatch } from "react-redux";
import { getItems, getNotContainItems, getMinMax, passwordStrength, submitIsDisabled } from "util/password";
import { Mixpanel } from "util/clickstreaming";
import { useParams } from "react-router-dom";

const FORM_ID = "recoveryPassword.step3";
const recoveryI18n = {
    password: "recoveryPassword",
    username: "recoveryUsername",
};

const recoveryText = {
    password: "contraseña",
    username: "usuario",
};

const ChangePassword = ({ exchangeToken, resetCode, isDesktop }) => {
    const dispatch = useDispatch();
    const { recoveryType } = useParams();
    const { maxLength, maxLengthValidationMsg, minLength, minLengthValidationMsg } = getMinMax();

    const initialValues = {
        password: "",
        passwordConfirmation: "",
    };

    const validationSchema = Yup.lazy((values) =>
        Yup.object().shape({
            password: Yup.string()
                .required(i18n.get("enrollment.step3.password.error.empty"))
                .min(minLength, minLengthValidationMsg)
                .max(maxLength, maxLengthValidationMsg)
                .matches(
                    new RegExp(config.get("core.userconfiguration.password.pattern"), "g"),
                    i18n.get("enrollment.step3.password.error.unfulfilledRules"),
                )
                .oneOf(
                    [values.passwordConfirmation],
                    i18n.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                ),
            passwordConfirmation: Yup.string()
                .matches(
                    new RegExp(config.get("core.userconfiguration.password.pattern"), "g"),
                    i18n.get("enrollment.step3.password.error.unfulfilledRules"),
                )
                .oneOf([values.password], i18n.get("enrollment.step3.password.error.passwordConfirmationMustMatch")),
        }),
    );

    const handleSubmit = (values, formikBag) => {
        Mixpanel.track(FORM_ID);
        dispatch(
            recoveryPasswordActions.recoveryPassStep3(
                values.password,
                values.passwordConfirmation,
                exchangeToken,
                resetCode,
                formikBag,
                recoveryType,
            ),
        );
    };

    const firstValidation = {
        description: minLengthValidationMsg,
        status: (password) => {
            if (!password) {
                return "INITIAL";
            }
            return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
        },
    };

    const getFirstItem = (password) => ({
        status: firstValidation.status(password),
        description: firstValidation.description,
    });

    return (
        <>
            {isDesktop && (
                <div className="text-center">
                    <Logo className="svg-image logo" />
                </div>
            )}
            <h1 className="text-center">
                <I18n
                    id={`${recoveryI18n[recoveryType]}.step1.header`}
                    defaultValue={`Recuperar mi ${recoveryText[recoveryType]}?`}
                />
            </h1>
            <br />
            <br />
            <Formik
                validateOnChange
                validateOnBlur
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}>
                {({ values, isSubmitting }) => (
                    <Form className="above-the-fold login-desktop-wrapper">
                        <Field
                            name="password"
                            idForm={FORM_ID}
                            component={Credential}
                            showStrength
                            strength={passwordStrength(values?.password)}
                            maxLength={maxLength}
                        />

                        <ToDoList items={[getFirstItem(values?.password)]} />
                        <ToDoList items={getItems(values?.password)} />
                        <I18n id="onboarding.password.notContainValidations" defaultValue="No debe contener:" />
                        <ToDoList items={getNotContainItems(values?.password)} />

                        <Field
                            name="passwordConfirmation"
                            idForm={FORM_ID}
                            component={Credential}
                            maxLength={maxLength}
                        />
                        <Button
                            label="global.save"
                            loading={isSubmitting}
                            type="submit"
                            bsStyle="primary"
                            disabled={submitIsDisabled({
                                firstValidation,
                                password: values?.password,
                                repeatedPassword: values?.passwordConfirmation,
                            })}
                            block
                        />
                    </Form>
                )}
            </Formik>
        </>
    );
};

ChangePassword.propTypes = {
    exchangeToken: string.isRequired,
    resetCode: string.isRequired,
    isDesktop: bool.isRequired,
};

export default ChangePassword;
